import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import { useAuth } from '../../AuthProvider'
import {
  getUsers, inviteUser, deactivateUser, reactivateUser, updateUserRole, getUserRoles,
  getCustomers, getAuthConfig,
} from '../../api'
import { settledList } from '../../utils/loadState'
import './SecurityManagement.css'

const ROLE_LABELS = {
  admin: 'Admin',
  consultant: 'Consultant',
  business_development: 'Business Dev',
  customer: 'Customer',
}

const roleLabel = role => ROLE_LABELS[role] || role

// The roles the server asks for a TOTP code (GET /api/auth/config); [] when MFA is off.
const mfaRoles = cfg => cfg?.mfa_required_roles || []
const mfaRequiredFor = (cfg, role) => mfaRoles(cfg).includes(role)
const roleList = roles => {
  const labels = roles.map(roleLabel)
  return labels.length > 1 ? `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}` : labels.join('')
}

// R-B: every statement on this page comes from GET /api/auth/config -- what the server
// actually enforces right now. It used to claim "MFA Required: Enforced" and "Supabase
// HS256 JWTs with AAL2 enforcement" while login was one shared password with no MFA.
function policyCards(cfg) {
  if (!cfg) return []
  const rl = cfg.login_rate_limit || {}
  const minutes = rl.window_seconds ? Math.round(rl.window_seconds / 60) : null
  const cards = []
  cards.push({ icon: '🔑', title: 'Per-user passwords', badge: 'Enforced',
    detail: 'Each user signs in with their own password. There is no shared access password.' })
  cards.push(mfaRoles(cfg).length > 0
    ? { icon: '🔐', title: 'Staff two-factor', badge: 'Enforced',
        detail: `${roleList(mfaRoles(cfg))} users must enter an authenticator (TOTP) code before any page or API answers them. Other roles are not asked for a code.` }
    : { icon: '🔐', title: 'Two-factor authentication', badge: 'Not enforced', off: true,
        detail: 'No second factor is required at sign-in.' })
  cards.push({ icon: '🎫', title: 'Invite-only access', badge: 'Enforced',
    detail: 'Only emails an admin has added to the user directory can sign in; anyone else is refused.' })
  cards.push({ icon: '⏱️', title: 'Sessions', badge: 'Active',
    detail: 'Per-user sessions use short-lived access tokens that refresh automatically. ' +
      (cfg.user_cache_seconds
        ? `Role, customers and active status are re-checked against the user directory on every request (changes made here apply on the next request; direct database edits within ${cfg.user_cache_seconds} s).`
        : 'The user directory is not connected right now, so every signed-in request is refused (503) until it reconnects.') })
  if (rl.failures_per_email) {
    cards.push({ icon: '🧱', title: 'Sign-in rate limit', badge: 'Enforced',
      detail: `${rl.failures_per_email} failed attempts per email and ${rl.failures_per_client} failed attempts per client per ${minutes} min, then sign-in is paused.` })
  }
  return cards
}

const ROLE_COLORS = {
  admin: { bg: '#ede9fe', color: '#7c3aed' },
  consultant: { bg: '#dbeefd', color: '#020c30' },
  business_development: { bg: '#e0e7f8', color: '#2d5bc0' },
}

export default function SecurityManagement() {
  const { profile } = useAuth()
  const myId = profile?.profile_id
  const [users, setUsers] = useState([])
  const [customers, setCustomers] = useState([])
  const [authConfig, setAuthConfig] = useState(null)
  // From GET /api/auth/roles, in the backend's order: [{ role, machine }]. Empty when it
  // could not be read -- the Role column then shows the badge only.
  const [roles, setRoles] = useState([])
  const [busyUserId, setBusyUserId] = useState(null)
  const [loading, setLoading] = useState(true)
  // A failed users/customers load is an error with a Retry -- never "0 users" or
  // "No customers created yet" (item 201).
  const [usersError, setUsersError] = useState(null)
  const [customersError, setCustomersError] = useState(null)
  const [filter, setFilter] = useState('all') // all | active | inactive
  const [search, setSearch] = useState('')

  // Invite modal
  const [showInvite, setShowInvite] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteName, setInviteName] = useState('')
  const [inviteRole, setInviteRole] = useState('consultant')
  const [inviteCustomers, setInviteCustomers] = useState([])
  const [inviteLoading, setInviteLoading] = useState(false)
  const [inviteError, setInviteError] = useState('')
  const [inviteSuccess, setInviteSuccess] = useState('')

  useEffect(() => { load() }, [])

  async function load() {
    try {
      const [u, c, cfg, r] = await Promise.allSettled(
        [getUsers(), getCustomers(), getAuthConfig(), getUserRoles()])
      const us = settledList(u, 'users')
      setUsers(us.items || [])
      setUsersError(us.error)
      if (cfg.status === 'fulfilled') setAuthConfig(cfg.value)
      setRoles(r.status === 'fulfilled' && Array.isArray(r.value?.roles) ? r.value.roles : [])
      const cu = settledList(c, 'contexts', 'customers')
      setCustomers(cu.items || [])
      setCustomersError(cu.error)
    } catch (err) {
      console.error('Failed to load users:', err)
    } finally {
      setLoading(false)
    }
  }

  async function handleInvite(e) {
    e.preventDefault()
    setInviteLoading(true)
    setInviteError('')
    setInviteSuccess('')
    try {
      const result = await inviteUser({
        email: inviteEmail,
        display_name: inviteName,
        role: inviteRole,
        customer_ids: inviteCustomers,
      })
      setInviteSuccess(`Invited ${result.email} — they will get an email to set their own password.`)
      setInviteEmail('')
      setInviteName('')
      setInviteRole('consultant')
      setInviteCustomers([])
      await load()
    } catch (err) {
      setInviteError(err.message || 'Failed to invite user.')
    } finally {
      setInviteLoading(false)
    }
  }

  async function handleDeactivate(userId, email) {
    if (!confirm(`Deactivate ${email}? They will lose access immediately.`)) return
    setBusyUserId(userId)
    try {
      await deactivateUser(userId)
      await load()
    } catch (err) {
      alert('Failed to deactivate: ' + err.message)
    } finally {
      setBusyUserId(null)
    }
  }

  async function handleReactivate(userId, email) {
    if (!confirm(`Reactivate ${email}? They will be able to sign in again.`)) return
    setBusyUserId(userId)
    try {
      await reactivateUser(userId)
      await load()
    } catch (err) {
      alert('Failed to reactivate: ' + err.message)
    } finally {
      setBusyUserId(null)
    }
  }

  // The select is controlled by u.role, so a cancelled or failed change leaves it as it was.
  async function handleRoleChange(u, newRole) {
    if (!newRole || newRole === u.role) return
    let msg = `Change ${u.email} from ${roleLabel(u.role)} to ${roleLabel(newRole)}?`
    if (mfaRequiredFor(authConfig, newRole) && !mfaRequiredFor(authConfig, u.role)) {
      msg += `\n\n${roleLabel(newRole)} users must use two-factor sign-in: they will be asked to set up an authenticator (TOTP) code on their next request.`
    }
    if (!confirm(msg)) return
    setBusyUserId(u.id)
    try {
      await updateUserRole(u.id, newRole)
      await load()
    } catch (err) {
      alert('Failed to change role: ' + err.message)
    } finally {
      setBusyUserId(null)
    }
  }

  const personRoles = roles.filter(r => !r.machine)
  // A machine account is offered only machine roles and a person only person roles; the
  // server refuses any change between the two (409).
  const isMachineRole = role => !!roles.find(r => r.role === role)?.machine
  const rolesOfKind = role => roles.filter(r => r.machine === isMachineRole(role))

  const filtered = users.filter(u => {
    if (filter === 'active' && !u.is_active) return false
    if (filter === 'inactive' && u.is_active) return false
    if (search) {
      const q = search.toLowerCase()
      return u.email?.toLowerCase().includes(q) || u.display_name?.toLowerCase().includes(q)
    }
    return true
  })

  const activeCount = users.filter(u => u.is_active).length
  const adminCount = users.filter(u => u.role === 'admin' && u.is_active).length
  const ownPasswordCount = users.filter(u => u.is_active && u.auth_linked).length

  const breadcrumbs = [
    { label: 'Admin', href: '/admin' },
    { label: 'Security' },
  ]

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs}
        actions={<>
          <Link to="/admin/roles" className="nav-btn">Role Configuration</Link>
          <Link to="/admin" className="nav-btn">Dashboard</Link>
        </>}
      />
      <div className="sm-page">
        {/* Stats Bar */}
        <div className="sm-stats-bar">
          <div className="sm-stat">
            <div className="sm-stat-value">{usersError ? '—' : users.length}</div>
            <div className="sm-stat-label">Total Users</div>
          </div>
          <div className="sm-stat">
            <div className="sm-stat-value sm-stat-green">{usersError ? '—' : activeCount}</div>
            <div className="sm-stat-label">Active</div>
          </div>
          <div className="sm-stat">
            <div className="sm-stat-value sm-stat-purple">{usersError ? '—' : adminCount}</div>
            <div className="sm-stat-label">Admins</div>
          </div>
          <div className="sm-stat">
            <div className="sm-stat-value sm-stat-amber">{usersError ? '—' : ownPasswordCount}</div>
            <div className="sm-stat-label">Own sign-in account</div>
          </div>
          <div style={{ flex: 1 }} />
          <button className="sm-invite-btn" onClick={() => { setShowInvite(true); setInviteError(''); setInviteSuccess('') }}>
            + Invite User
          </button>
        </div>

        {/* Policy Cards -- rendered from /api/auth/config, never hard-coded */}
        <div className="sm-policy-grid">
          {!authConfig && (
            <div className="sm-policy-card">
              <div className="sm-policy-info">
                <div className="sm-policy-title">Security policy</div>
                <div className="sm-policy-detail">Could not read the server's security settings.</div>
              </div>
            </div>
          )}
          {policyCards(authConfig).map(card => (
            <div className="sm-policy-card" key={card.title}>
              <div className="sm-policy-icon">{card.icon}</div>
              <div className="sm-policy-info">
                <div className="sm-policy-title">{card.title}</div>
                <div className="sm-policy-detail">{card.detail}</div>
              </div>
              <div className={`sm-policy-badge ${card.off ? 'sm-policy-active' : 'sm-policy-enforced'}`}>{card.badge}</div>
            </div>
          ))}
        </div>

        {/* Filters & Search */}
        <div className="sm-toolbar">
          <div className="sm-filter-chips">
            {['all', 'active', 'inactive'].map(f => (
              <button key={f} className={`sm-filter-chip ${filter === f ? 'active' : ''}`}
                onClick={() => setFilter(f)}>
                {f === 'all' ? 'All' : f === 'active' ? 'Active' : 'Inactive'}
                {f === 'all' && <span className="sm-chip-count">{users.length}</span>}
                {f === 'active' && <span className="sm-chip-count">{activeCount}</span>}
                {f === 'inactive' && <span className="sm-chip-count">{users.length - activeCount}</span>}
              </button>
            ))}
          </div>
          <input className="sm-search" placeholder="Search by name or email…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {/* User Table */}
        {loading ? (
          <div className="sm-loading">Loading users…</div>
        ) : usersError ? (
          <LoadError what="users" error={usersError} onRetry={load} />
        ) : (
          <div className="sm-table-wrap">
            <table className="sm-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Customers</th>
                  <th>Sign-in</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id} className={!u.is_active ? 'sm-row-inactive' : ''}>
                    <td>
                      <div className="sm-user-cell">
                        <div className="sm-user-avatar">{(u.display_name || u.email || '?')[0].toUpperCase()}</div>
                        <div>
                          <div className="sm-user-name">{u.display_name}</div>
                          <div className="sm-user-email">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {roles.length > 0 ? (
                        <select className="sm-role-select" value={u.role}
                          aria-label={`Role for ${u.email}`}
                          disabled={u.id === myId || busyUserId === u.id}
                          title={u.id === myId ? 'You cannot change your own role' : undefined}
                          onChange={e => handleRoleChange(u, e.target.value)}>
                          {!roles.some(r => r.role === u.role) && (
                            <option value={u.role}>{roleLabel(u.role)}</option>
                          )}
                          {rolesOfKind(u.role).map(r => (
                            <option key={r.role} value={r.role}>{roleLabel(r.role)}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="sm-role-badge" style={{
                          background: ROLE_COLORS[u.role]?.bg || '#f3f4f6',
                          color: ROLE_COLORS[u.role]?.color || '#6b7280',
                        }}>{roleLabel(u.role)}</span>
                      )}
                    </td>
                    <td>
                      <span className="sm-customer-count">
                        {u.customer_ids?.length || 0}
                      </span>
                    </td>
                    <td>
                      <span className="sm-mfa-status">{u.auth_linked ? 'Own password' : 'Shared password'}</span>
                    </td>
                    <td>
                      {u.is_active
                        ? <span className="sm-status-badge sm-status-active">Active</span>
                        : <span className="sm-status-badge sm-status-inactive">Inactive</span>
                      }
                    </td>
                    <td>
                      {u.is_active ? (
                        <button className="sm-action-btn sm-action-deactivate"
                          disabled={u.id === myId || busyUserId === u.id}
                          title={u.id === myId ? 'You cannot deactivate your own account' : undefined}
                          onClick={() => handleDeactivate(u.id, u.email)}>
                          Deactivate
                        </button>
                      ) : (
                        <button className="sm-action-btn sm-action-reactivate"
                          disabled={busyUserId === u.id}
                          onClick={() => handleReactivate(u.id, u.email)}>
                          Reactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="sm-empty">No users found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite Modal */}
      {showInvite && (
        <div className="sm-overlay" onClick={e => { if (e.target === e.currentTarget) setShowInvite(false) }}>
          <div className="sm-modal">
            <div className="sm-modal-header">
              <h3>Invite User</h3>
              <button className="sm-modal-close" onClick={() => setShowInvite(false)}>✕</button>
            </div>
            {inviteError && <div className="sm-alert sm-alert-error">{inviteError}</div>}
            {inviteSuccess && <div className="sm-alert sm-alert-success">{inviteSuccess}</div>}
            <form onSubmit={handleInvite} className="sm-invite-form">
              <div className="sm-form-group">
                <label className="sm-label">Email</label>
                <input type="email" className="sm-input" placeholder="user@example.com"
                  value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} required autoFocus />
              </div>
              <div className="sm-form-group">
                <label className="sm-label">Display Name</label>
                <input type="text" className="sm-input" placeholder="Jane Smith"
                  value={inviteName} onChange={e => setInviteName(e.target.value)} required />
              </div>
              <div className="sm-form-group">
                <label className="sm-label">Role</label>
                <select className="sm-select" value={inviteRole} onChange={e => setInviteRole(e.target.value)}>
                  {(personRoles.length > 0 ? personRoles : [{ role: inviteRole }]).map(r => (
                    <option key={r.role} value={r.role}>{roleLabel(r.role)}</option>
                  ))}
                </select>
                {mfaRequiredFor(authConfig, inviteRole) && (
                  <div className="sm-role-note">{roleLabel(inviteRole)} users are asked to set up an authenticator (TOTP) code at their first sign-in.</div>
                )}
              </div>
              <div className="sm-form-group">
                <label className="sm-label">Assign Customers (optional)</label>
                <div className="sm-customer-checklist">
                  {customers.map(c => {
                    const cid = c.customer_id || c.id
                    const name = c.company_name || c.name || cid
                    const checked = inviteCustomers.includes(cid)
                    return (
                      <label key={cid} className="sm-customer-check">
                        <input type="checkbox" checked={checked}
                          onChange={() => {
                            setInviteCustomers(prev =>
                              checked ? prev.filter(x => x !== cid) : [...prev, cid]
                            )
                          }} />
                        {name}
                      </label>
                    )
                  })}
                  {customersError && <LoadError inline what="customers" error={customersError} onRetry={load} />}
                  {!customersError && customers.length === 0 && <div className="sm-no-customers">No customers created yet</div>}
                </div>
              </div>
              <div className="sm-modal-footer">
                <button type="button" className="sm-btn-cancel" onClick={() => setShowInvite(false)}>Cancel</button>
                <button type="submit" className="sm-btn-invite" disabled={inviteLoading}>
                  {inviteLoading ? 'Inviting…' : 'Send Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
